// สแกนกระดาษคำตอบ — หน้าเดียวทั้งกล้องมือถือและไฟล์ (PDF จากเครื่องสแกน / รูปถ่าย) ใช้กติกาชุดเดียวกัน
//
// อ่านในเบราว์เซอร์ทั้งหมด (omr/reader.js) ภาพไม่ถูกส่งขึ้นเซิร์ฟเวอร์ สิ่งที่บันทึกคือคำตอบรายข้อเท่านั้น
// ผ่าน endpoint เดียวกับการกรอกมือ (source = 'scan' — แผ่นว่างได้ศูนย์ และคะแนนอัตนัยเดิมไม่ถูกแตะ)
//
// แผ่นที่ชัด (รู้ตัวนักเรียนจากแถบรหัส ชัดทุกข้อ ไม่มีคำตอบเดิมที่ต่าง) บันทึกเองทันทีแล้วขึ้นคะแนน
// ที่เหลือรอครูดูในหน้าตรวจ (ScanReview) — กติกาทั้งหมดอยู่ใน scanSession.js
// ตัวอ่านที่เดาเงียบๆ แล้วลงคะแนนผิดแย่กว่าตัวอ่านที่ถามบ่อย
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Badge, Button, ButtonGroup, Card, ProgressBar } from 'react-bootstrap'
import { Link, useParams } from 'react-router-dom'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { API, isObjective, labelSet, roomLabel, showError } from './examUtils'
import { readSheet } from './omr/reader'
import { isSupported, loadPages, thumbnail } from './omr/loadPages'
import { grayToCanvas } from './scanCrop'
import { buildItem, decide, responseOf, reviewReason, scoreText, withCrops } from './scanSession'
import ScanCamera, { cameraAvailable, tone } from './ScanCamera'
import ScanReview from './ScanReview'

const defaultMode = () => {
  try {
    return cameraAvailable() && window.matchMedia('(pointer: coarse)').matches ? 'camera' : 'file'
  } catch { return 'file' }
}

export default function ExamScan({ initialMode }) {
  const { examId } = useParams()
  const [, setSkin] = useState('')
  const [data, setData] = useState(null)
  const [mode, setMode] = useState(() => initialMode || defaultMode())
  const [items, setItems] = useState([])            // ใหม่สุดอยู่บน
  const [reviewId, setReviewId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [progress, setProgress] = useState(null)    // { done, running }
  const [dragOver, setDragOver] = useState(false)
  const fileRef = useRef(null)
  const dataRef = useRef(null)

  const load = useCallback(() => axiosInstance.get(`${API}/exams/${examId}/results`)
    .then((res) => { dataRef.current = res.data; setData(res.data) })
    .catch((err) => showError(err, 'โหลดข้อสอบไม่สำเร็จ')), [examId])
  useEffect(() => { load() }, [load])

  const objective = useMemo(() => (data ? data.questions.filter(isObjective) : []), [data])
  const objectiveRef = useRef(objective)
  useEffect(() => { objectiveRef.current = objective }, [objective])
  const labels = labelSet(data?.exam)

  const patchItems = (updates) => setItems((prev) => prev.map((i) => (updates.has(i.id) ? { ...i, ...updates.get(i.id) } : i)))

  // ---- บันทึก ----

  // entries = [{ id, studyId, answers }] — รวมเป็นคำขอเดียวต่อห้อง แล้วโหลดคำตอบจาก server ใหม่
  // (ไม่ประกอบเองในหน้า แผ่นถัดไปจะได้เทียบกับของที่บันทึกจริง) คะแนนมาจาก server ที่คิดด้วยตัวเดียวกับหน้าผลสอบ
  const saveEntries = useCallback(async (entries) => {
    const d = dataRef.current
    const obj = objectiveRef.current
    const byRoom = new Map()
    for (const e of entries) {
      const st = d.students.find((s) => s.study_id === e.studyId)
      const list = byRoom.get(st.teach_id) || []
      list.push({ ...e, student: st, replaced: !!st.answers })
      byRoom.set(st.teach_id, list)
    }
    const updates = new Map()
    let failed = null
    for (const [teachId, list] of byRoom) {
      try {
        const res = await axiosInstance.put(`${API}/exams/${examId}/rooms/${teachId}/responses`, {
          source: 'scan',
          responses: list.map((e) => responseOf(e.studyId, e.answers, obj)),
        })
        for (const e of list) {
          updates.set(e.id, {
            state: 'saved', studyId: e.studyId, answers: e.answers, replaced: e.replaced,
            score: res.data.scores?.[e.studyId] || null, total: res.data.total_points, saveError: null,
          })
        }
      } catch (err) {
        failed = err
        const message = err?.response?.data?.message || err.message
        for (const e of list) updates.set(e.id, { state: 'review', saveError: `บันทึกไม่สำเร็จ: ${message}` })
      }
    }
    patchItems(updates)
    await load()
    return { failed }
  }, [examId, load])

  // ---- กล้อง ----

  const onCameraRead = useCallback(async (res, canvas) => {
    const d = dataRef.current
    if (!d) return
    const obj = objectiveRef.current
    let item = buildItem({ res, students: d.students, objective: obj, thumb: thumbnail(canvas), label: 'กล้อง', source: 'camera' })
    if (item.state === 'problem') {
      setItems((prev) => [item, ...prev])
      tone(false)
      return
    }
    const next = decide(item, d.students, obj)
    if (next === 'same') {
      setItems((prev) => [{ ...item, state: 'same' }, ...prev])
      tone(true)
      return
    }
    if (next === 'review') {
      item = withCrops(item, canvas)
      setItems((prev) => [item, ...prev])
      setReviewId(item.id)
      tone(false)
      return
    }
    setItems((prev) => [{ ...item, state: 'saving' }, ...prev])
    const { failed } = await saveEntries([{ id: item.id, studyId: item.studyId, answers: item.answers }])
    tone(!failed)
  }, [saveEntries])

  // ---- ไฟล์ ----

  // อ่านทุกหน้าก่อน แล้วค่อยบันทึกแผ่นที่ชัดรวดเดียว — หลายแผ่นของคนเดียวกันในไฟล์ต้องให้ครูเลือกเอง
  const onFiles = async (fileList) => {
    const files = [...fileList].filter(isSupported)
    const d = dataRef.current
    if (!files.length || !d) return
    const obj = objectiveRef.current
    setProgress({ done: 0, running: true })
    const batch = []
    try {
      for await (const page of loadPages(files)) {
        const res = readSheet(page.img, d.exam, d.questions)
        let item = buildItem({ res, students: d.students, objective: obj, thumb: page.thumb, label: page.label, source: 'file' })
        const next = decide(item, d.students, obj)
        if (next === 'review') item = withCrops(item, grayToCanvas(page.img))
        else if (next !== 'review' && item.state !== 'problem') item = { ...item, state: next === 'same' ? 'same' : 'saving' }
        batch.push(item)
        setItems((prev) => [item, ...prev])
        setProgress((p) => ({ ...p, done: p.done + 1 }))
        await new Promise((r) => setTimeout(r, 0))   // ให้หน้าจอได้วาดความคืบหน้า
      }
    } catch (err) {
      showError(err, 'เปิดไฟล์ไม่สำเร็จ')
    } finally {
      if (fileRef.current) fileRef.current.value = ''
    }

    // แผ่นซ้ำ: คนเดียวกันหลายแผ่นในไฟล์นี้ หรือมีแผ่นของคนนี้รอตรวจอยู่แล้ว — ไม่บันทึกเองสักแผ่น
    const waitingIds = new Set(items.filter((i) => i.state === 'review' && i.studyId).map((i) => i.studyId))
    const count = new Map()
    for (const i of batch) if (i.studyId && i.state !== 'problem') count.set(i.studyId, (count.get(i.studyId) || 0) + 1)
    const isDupe = (i) => i.studyId && (count.get(i.studyId) > 1 || waitingIds.has(i.studyId))
    const updates = new Map()
    for (const i of batch) {
      if (i.state !== 'problem' && isDupe(i)) updates.set(i.id, { state: 'review', dupe: true })
    }
    patchItems(updates)
    const toSave = batch.filter((i) => i.state === 'saving' && !updates.has(i.id))
    if (toSave.length) {
      const { failed } = await saveEntries(toSave.map((i) => ({ id: i.id, studyId: i.studyId, answers: i.answers })))
      if (failed) showError(failed, 'บันทึกบางแผ่นไม่สำเร็จ')
    }
    setProgress((p) => ({ ...p, running: false }))
  }

  // ---- ตรวจ ----

  const waiting = items.filter((i) => i.state === 'review')
  // แผ่นที่รอ ตรวจจากเก่าไปใหม่ (ตามลำดับหน้าในไฟล์)
  const queue = [...waiting].reverse()
  const reviewing = items.find((i) => i.id === reviewId && i.state === 'review')

  const nextAfter = (id) => (mode === 'file' ? queue.find((i) => i.id !== id)?.id ?? null : null)

  const onReviewSave = async (studyId, answers) => {
    const id = reviewing.id
    setSaving(true)
    const { failed } = await saveEntries([{ id, studyId, answers }])
    setSaving(false)
    if (failed) {
      showError(failed, 'บันทึกไม่สำเร็จ')
      return
    }
    tone(true)
    setReviewId(nextAfter(id))
  }

  const onReviewDiscard = () => {
    const id = reviewing.id
    setItems((prev) => prev.filter((i) => i.id !== id))
    setReviewId(nextAfter(id))
  }

  const removeItem = (id) => setItems((prev) => prev.filter((i) => i.id !== id))

  if (!data) return (<><Header onSkin={setSkin} /><div className="main main-app p-2 p-lg-4" /></>)

  const students = data.students
  const activeCount = students.filter((s) => String(s.st_status) !== '0' && s.transfer_room_id == null).length
  const answeredCount = students.filter((s) => s.answers).length
  const savedCount = items.filter((i) => i.state === 'saved').length
  const problemCount = items.filter((i) => i.state === 'problem').length
  const latest = mode === 'camera' ? items.find((i) => i.source === 'camera') : null

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-2 p-lg-4">
        <div className="mb-3">
          <Link to={`/teaching/exams/${examId}`} className="fs-sm"><i className="ri-arrow-left-line"></i> {data.exam.exam_name}</Link>
          <h4 className="main-title mb-0">{reviewing ? 'ตรวจก่อนบันทึก' : 'สแกนกระดาษคำตอบ'}</h4>
        </div>

        {reviewing && (
          <ScanReview
            key={reviewing.id}
            item={reviewing}
            exam={data.exam}
            students={students}
            objective={objective}
            labels={labels}
            saving={saving}
            position={queue.length > 1 ? { index: queue.findIndex((i) => i.id === reviewing.id) + 1, count: queue.length } : null}
            discardLabel={mode === 'camera' ? 'ข้าม' : 'ไม่บันทึกแผ่นนี้'}
            onSave={onReviewSave}
            onDiscard={onReviewDiscard}
          />
        )}

        <div className="mx-auto" style={{ maxWidth: 720, display: reviewing ? 'none' : 'block' }}>
          <Card className="card-one mb-3">
            <Card.Body className="py-2">
              <div className="d-flex align-items-baseline mb-1">
                <span className="text-secondary fs-sm me-auto">มีคำตอบแล้ว</span>
                <b className="fs-5">{answeredCount}/{activeCount}</b><span className="fs-sm ms-1">คน</span>
              </div>
              <ProgressBar variant="success" now={activeCount ? (answeredCount / activeCount) * 100 : 0} style={{ height: 8 }} />
              {items.length > 0 && (
                <div className="fs-xs text-secondary mt-1">
                  รอบนี้: บันทึก {savedCount} แผ่น
                  {waiting.length > 0 && <> · <span className="text-warning-emphasis fw-semibold">รอตรวจ {waiting.length}</span></>}
                  {problemCount > 0 && <> · <span className="text-danger">อ่านไม่ได้ {problemCount}</span></>}
                </div>
              )}
            </Card.Body>
          </Card>

          <ButtonGroup className="w-100 mb-3">
            <Button variant={mode === 'camera' ? 'primary' : 'outline-primary'} onClick={() => setMode('camera')}>
              <i className="ri-camera-line"></i> กล้อง
            </Button>
            <Button variant={mode === 'file' ? 'primary' : 'outline-primary'} onClick={() => setMode('file')}>
              <i className="ri-file-pdf-2-line"></i> ไฟล์ PDF / รูป
            </Button>
          </ButtonGroup>

          {mode === 'camera' ? (
            <>
              <ScanCamera exam={data.exam} questions={data.questions} paused={!!reviewing} onRead={onCameraRead} />
              {latest && <LatestResult item={latest} students={students} totalPoints={data.total_points} onReview={() => setReviewId(latest.id)} />}
              {!items.length && (
                <div className="fs-xs text-secondary text-center mt-2">
                  วางกระดาษบนพื้นเรียบสีเข้ม แสงสม่ำเสมอ ให้เห็นทั้งแผ่น · ได้ยินเสียงแล้ววางแผ่นถัดไปได้เลย ·
                  อ่านในเครื่องนี้ ไม่ได้ส่งภาพขึ้นเซิร์ฟเวอร์
                </div>
              )}
            </>
          ) : (
            <Card
              className={`card-one mb-3 border-2 ${dragOver ? 'border-primary' : ''}`}
              style={{ borderStyle: 'dashed' }}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); if (!progress?.running) onFiles(e.dataTransfer.files) }}
            >
              <Card.Body className="text-center py-4">
                <input ref={fileRef} type="file" multiple hidden accept="application/pdf,image/jpeg,image/png,image/webp"
                  onChange={(e) => onFiles(e.target.files)} />
                {progress?.running ? (
                  <ProgressBar animated now={100} label={`กำลังอ่าน… ${progress.done} แผ่น`} />
                ) : (
                  <>
                    <Button size="lg" onClick={() => fileRef.current?.click()}>
                      <i className="ri-upload-2-line"></i> เลือกไฟล์ PDF หรือรูป
                    </Button>
                    <div className="fs-sm text-secondary mt-2">หรือลากไฟล์มาวางตรงนี้ · เลือกหลายไฟล์ได้ หลายห้องปนกันได้</div>
                  </>
                )}
                <div className="fs-xs text-secondary mt-2">
                  สแกนขาวดำหรือเทา 200–300 dpi ได้ผลดีที่สุด · แผ่นที่อ่านชัดบันทึกให้เอง แผ่นที่ไม่แน่ใจรอให้ตรวจ ·
                  อ่านในเครื่องนี้ ไม่ได้ส่งภาพขึ้นเซิร์ฟเวอร์
                </div>
              </Card.Body>
            </Card>
          )}

          {items.length > 0 && (
            <Card className="card-one mt-3">
              <Card.Header className="fw-semibold">แผ่นในรอบนี้ ({items.length})</Card.Header>
              <div>
                {items.map((item) => (
                  <ItemRow key={item.id} item={item} students={students} objective={objective} totalPoints={data.total_points}
                    onReview={() => setReviewId(item.id)} onRemove={() => removeItem(item.id)} />
                ))}
              </div>
            </Card>
          )}

          {waiting.length > 0 && (
            <div className="py-2 bg-body" style={{ position: 'sticky', bottom: 0, zIndex: 5 }}>
              <Button variant="warning" size="lg" className="w-100 fw-semibold" onClick={() => setReviewId(queue[0].id)}>
                <i className="ri-error-warning-line"></i> ตรวจแผ่นที่รอ ({waiting.length})
              </Button>
            </div>
          )}
        </div>
        <Footer />
      </div>
    </>
  )
}

// ---- ผลล่าสุดใต้กล้อง: ตัวใหญ่ให้เห็นจากระยะแขน ----

function LatestResult({ item, students, totalPoints, onReview }) {
  const student = students.find((s) => s.study_id === item.studyId)
  const who = student ? `${student.st_name} · ${roomLabel(student)} เลขที่ ${student.seat}` : item.label
  if (item.state === 'problem') {
    return <div className="alert alert-danger text-center mt-2 mb-0 fw-semibold">{item.message}</div>
  }
  if (item.state === 'review') {
    return (
      <div className="alert alert-warning d-flex align-items-center gap-2 mt-2 mb-0">
        <div className="me-auto"><div className="fw-semibold">{who}</div><div className="fs-sm">{item.saveError || 'รอตรวจก่อนบันทึก'}</div></div>
        <Button size="sm" variant="warning" onClick={onReview}>ตรวจ</Button>
      </div>
    )
  }
  const sc = scoreText(item.state === 'same' ? student?.score : item.score, item.state === 'same' ? totalPoints : item.total)
  return (
    <div className="alert alert-success d-flex align-items-center gap-3 mt-2 mb-0">
      <i className="ri-checkbox-circle-fill fs-1 text-success"></i>
      <div className="me-auto">
        <div className="fw-semibold">{who}</div>
        <div className="fs-sm">
          {item.state === 'saving' ? 'กำลังบันทึก…' : item.state === 'same' ? 'บันทึกไว้แล้ว คำตอบตรงกัน' : item.replaced ? 'บันทึกแทนของเดิมแล้ว' : 'บันทึกแล้ว'}
          {sc?.note && <> · {sc.note}</>}
        </div>
      </div>
      {sc && <div className="text-end"><div className="fs-2 fw-bold lh-1">{sc.main}</div><div className="fs-xs">คะแนน</div></div>}
    </div>
  )
}

// ---- แถวในรายการรอบนี้ ----

function ItemRow({ item, students, objective, totalPoints, onReview, onRemove }) {
  const student = students.find((s) => s.study_id === item.studyId)
  const title = student ? student.st_name : item.label
  const sub = student ? `${roomLabel(student)} เลขที่ ${student.seat}` : null
  const row = (icon, color, text, right) => (
    <div className="d-flex align-items-center gap-3 px-3 py-2 border-bottom">
      <i className={`${icon} fs-4 text-${color}`}></i>
      <div className="me-auto" style={{ minWidth: 0 }}>
        <div className="fw-semibold text-truncate">{title}{sub && <span className="fw-normal text-secondary fs-sm"> · {sub}</span>}</div>
        <div className="fs-xs text-secondary">{text}</div>
      </div>
      {right}
    </div>
  )
  switch (item.state) {
    case 'saving':
      return row('ri-loader-4-line', 'secondary', 'กำลังบันทึก…', null)
    case 'saved':
    case 'same': {
      const sc = scoreText(item.state === 'same' ? student?.score : item.score, item.state === 'same' ? totalPoints : item.total)
      const text = [item.state === 'same' ? 'บันทึกไว้แล้ว ตรงกัน' : item.replaced ? 'บันทึกแทนของเดิม' : 'บันทึกแล้ว', sc?.note].filter(Boolean).join(' · ')
      return row('ri-checkbox-circle-fill', 'success', text,
        sc && <Badge bg="success" className="fs-6">{sc.main}</Badge>)
    }
    case 'review':
      return row('ri-error-warning-fill', 'warning', item.saveError || reviewReason(item, student, objective),
        <Button size="sm" variant="warning" onClick={onReview}>ตรวจ</Button>)
    default:
      return row('ri-close-circle-fill', 'danger', item.message,
        <Button size="sm" variant="link" className="text-secondary" aria-label="เอาออก" onClick={onRemove}><i className="ri-close-line"></i></Button>)
  }
}
